import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  QrCode,
  Users,
  Clock,
  Plus,
  Play,
  Pause,
  RotateCcw,
  Trash2,
  Settings,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Volume2,
  ChevronRight,
  Download,
  BarChart3,
  Timer,
  UserX,
  Building2,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';

const FacilityQueue = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [queues, setQueues] = useState([]);
  const [facilityStats, setFacilityStats] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [queueStats, setQueueStats] = useState(null);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  const [createForm, setCreateForm] = useState({
    name: '',
    queue_type: 'facility',
    service_name: '',
    doctor_id: '',
    doctor_name: '',
    estimated_service_time: 15,
    max_capacity: '',
    is_active: true
  });
  
  const [professionals, setProfessionals] = useState([]);

  const loadQueues = useCallback(async () => {
    try {
      const [queuesRes, statsRes] = await Promise.all([
        api.get('/queue/facility'),
        api.get('/queue/facility/stats')
      ]);
      setQueues(queuesRes.data.queues || []);
      setFacilityStats(statsRes.data);
    } catch (error) {
      console.error('Error loading queues:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProfessionals = useCallback(async () => {
    try {
      const response = await api.get('/facility/professionals');
      setProfessionals(response.data.professionals || []);
    } catch (error) {
      console.error('Error loading professionals:', error);
    }
  }, []);

  const loadQueueStatus = useCallback(async (queueId) => {
    try {
      const response = await api.get(`/queue/${queueId}/status`);
      setQueueStatus(response.data);
    } catch (error) {
      console.error('Error loading queue status:', error);
    }
  }, []);

  const loadQueueStats = useCallback(async (queueId) => {
    try {
      const response = await api.get(`/queue/${queueId}/stats?days=7`);
      setQueueStats(response.data);
    } catch (error) {
      console.error('Error loading queue stats:', error);
    }
  }, []);

  useEffect(() => {
    loadQueues();
    loadProfessionals();
  }, [loadQueues, loadProfessionals]);

  useEffect(() => {
    if (selectedQueue) {
      loadQueueStatus(selectedQueue.id);
      const interval = setInterval(() => loadQueueStatus(selectedQueue.id), 10000);
      return () => clearInterval(interval);
    }
  }, [selectedQueue, loadQueueStatus]);

  const handleCreateQueue = async (e) => {
    e.preventDefault();
    if (!createForm.name) {
      toast.error('Veuillez entrer un nom');
      return;
    }
    
    setCreating(true);
    try {
      await api.post('/queue/create', {
        ...createForm,
        estimated_service_time: parseInt(createForm.estimated_service_time),
        max_capacity: createForm.max_capacity ? parseInt(createForm.max_capacity) : null
      });
      toast.success('File créée !');
      setShowCreateModal(false);
      setCreateForm({
        name: '', queue_type: 'facility', service_name: '', doctor_id: '', doctor_name: '',
        estimated_service_time: 15, max_capacity: '', is_active: true
      });
      loadQueues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setCreating(false);
    }
  };

  const handleCallNext = async () => {
    if (!selectedQueue) return;
    setProcessing(true);
    try {
      const response = await api.post(`/queue/${selectedQueue.id}/call-next`);
      if (response.data.next_ticket) {
        toast.success(`Numéro ${response.data.next_ticket.ticket_number} appelé !`);
      } else {
        toast.info('Aucun patient en attente');
      }
      loadQueueStatus(selectedQueue.id);
      loadQueues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateTicketStatus = async (ticketId, status) => {
    if (!selectedQueue) return;
    try {
      await api.post(`/queue/${selectedQueue.id}/ticket/${ticketId}/status?status=${status}`);
      toast.success('Statut mis à jour');
      loadQueueStatus(selectedQueue.id);
      loadQueues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleQueue = async (queueId, isActive) => {
    try {
      await api.put(`/queue/${queueId}`, { is_active: !isActive });
      toast.success(isActive ? 'File désactivée' : 'File activée');
      loadQueues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleResetQueue = async () => {
    if (!selectedQueue) return;
    if (!confirm('Réinitialiser la file ? Tous les tickets en attente seront annulés.')) return;
    
    try {
      await api.post(`/queue/${selectedQueue.id}/reset`);
      toast.success('File réinitialisée');
      loadQueueStatus(selectedQueue.id);
      loadQueues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteQueue = async (queueId) => {
    if (!confirm('Supprimer cette file définitivement ?')) return;
    
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success('File supprimée');
      if (selectedQueue?.id === queueId) {
        setSelectedQueue(null);
        setQueueStatus(null);
      }
      loadQueues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const openQRModal = (queue) => {
    setSelectedQueue(queue);
    setShowQRModal(true);
  };

  const openStatsModal = async (queue) => {
    setSelectedQueue(queue);
    await loadQueueStats(queue.id);
    setShowStatsModal(true);
  };

  const downloadQRCode = () => {
    if (!selectedQueue?.qr_code) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${selectedQueue.qr_code}`;
    link.download = `qr-${selectedQueue.name.replace(/\s+/g, '-')}.png`;
    link.click();
  };

  const printQRCode = async () => {
    if (!selectedQueue) return;
    
    try {
      // Get high-res QR code
      const response = await api.get(`/queue/${selectedQueue.id}/qr-printable`);
      const data = response.data;
      
      // Create print window
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>QR Code - ${data.queue_name}</title>
          <style>
            @media print {
              body { margin: 0; padding: 20mm; }
              .no-print { display: none; }
            }
            body {
              font-family: Arial, sans-serif;
              text-align: center;
              padding: 40px;
            }
            .header {
              color: #DC2626;
              font-size: 32px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .facility {
              color: #1A237E;
              font-size: 24px;
              margin-bottom: 30px;
            }
            .queue-name {
              background: #f1f5f9;
              padding: 15px 30px;
              border-radius: 12px;
              font-size: 20px;
              font-weight: 600;
              color: #334155;
              display: inline-block;
              margin-bottom: 30px;
            }
            .qr-container {
              margin: 30px auto;
              padding: 20px;
              border: 3px solid #DC2626;
              border-radius: 20px;
              display: inline-block;
            }
            .qr-code {
              width: 300px;
              height: 300px;
            }
            .instructions {
              margin-top: 30px;
              font-size: 18px;
              color: #64748b;
            }
            .url {
              margin-top: 20px;
              font-size: 12px;
              color: #94a3b8;
              word-break: break-all;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #e2e8f0;
              font-size: 12px;
              color: #94a3b8;
            }
            .print-btn {
              background: #DC2626;
              color: white;
              border: none;
              padding: 15px 40px;
              font-size: 18px;
              border-radius: 30px;
              cursor: pointer;
              margin-top: 30px;
            }
            .print-btn:hover {
              background: #B91C1C;
            }
          </style>
        </head>
        <body>
          <div class="header">DOCKHTA</div>
          <div class="facility">${data.facility_name || ''}</div>
          <div class="queue-name">${data.queue_name}</div>
          
          <div class="qr-container">
            <img src="${data.qr_code_hd}" alt="QR Code" class="qr-code" />
          </div>
          
          <div class="instructions">
            📱 Scannez ce QR code pour prendre un ticket
          </div>
          
          <div class="url">${data.qr_url}</div>
          
          <button class="print-btn no-print" onclick="window.print()">
            🖨️ Imprimer
          </button>
          
          <div class="footer">
            DOCKHTA - Votre santé, notre priorité<br>
            www.dockhta.com | +237 695 06 57 08
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      
    } catch (error) {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="section-padding py-6" data-testid="facility-queue-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Files d&apos;attente
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Gérez les files d&apos;attente avec QR codes
            </p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="create-queue-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle file
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <QrCode className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{facilityStats?.queues_count || 0}</p>
                  <p className="text-xs text-slate-500">Files actives</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{facilityStats?.currently_waiting || 0}</p>
                  <p className="text-xs text-slate-500">En attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{facilityStats?.today_completed || 0}</p>
                  <p className="text-xs text-slate-500">Servis aujourd&apos;hui</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Timer className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{facilityStats?.today_tickets || 0}</p>
                  <p className="text-xs text-slate-500">Tickets aujourd&apos;hui</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Queues List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Mes files</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {queues.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <QrCode className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Aucune file créée</p>
                    <Button
                      size="sm"
                      onClick={() => setShowCreateModal(true)}
                      className="mt-4"
                    >
                      Créer une file
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {queues.map((queue) => (
                      <div
                        key={queue.id}
                        className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                          selectedQueue?.id === queue.id ? 'bg-red-50 border-l-4 border-l-red-500' : ''
                        }`}
                        onClick={() => {
                          setSelectedQueue(queue);
                          loadQueueStatus(queue.id);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-sm">{queue.name}</h3>
                              <Badge className={queue.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                                {queue.is_active ? 'Active' : 'Fermée'}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {queue.queue_type === 'doctor' && queue.doctor_name}
                              {queue.queue_type === 'service' && queue.service_name}
                              {queue.queue_type === 'facility' && 'File générale'}
                            </p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                              <Users className="w-3 h-3" />
                              <span>{queue.waiting_count || 0} en attente</span>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Queue Details */}
          <div className="lg:col-span-2">
            {!selectedQueue ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <QrCode className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-600">Sélectionnez une file</h3>
                  <p className="text-slate-500 text-sm mt-1">
                    Cliquez sur une file pour la gérer
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Queue Header */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold">{selectedQueue.name}</h2>
                        <p className="text-sm text-slate-500">
                          Numéro actuel: <span className="font-bold text-2xl text-red-500">{queueStatus?.queue?.now_serving || 0}</span>
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openQRModal(selectedQueue)}
                        >
                          <QrCode className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openStatsModal(selectedQueue)}
                        >
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleQueue(selectedQueue.id, selectedQueue.is_active)}
                        >
                          {selectedQueue.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleResetQueue}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500"
                          onClick={() => handleDeleteQueue(selectedQueue.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Call Next Button */}
                <Button
                  className="w-full h-16 text-lg bg-green-500 hover:bg-green-600"
                  onClick={handleCallNext}
                  disabled={processing || !selectedQueue.is_active}
                >
                  {processing ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <Volume2 className="w-6 h-6 mr-3" />
                      Appeler le suivant
                    </>
                  )}
                </Button>

                {/* Currently Serving */}
                {queueStatus?.currently_serving && (
                  <Card className="border-2 border-green-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">En cours de service</p>
                          <p className="text-3xl font-bold text-green-600">
                            N°{queueStatus.currently_serving.ticket_number}
                          </p>
                          <p className="font-medium">{queueStatus.currently_serving.patient_name}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleUpdateTicketStatus(queueStatus.currently_serving.id, 'completed')}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Terminé
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateTicketStatus(queueStatus.currently_serving.id, 'no_show')}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Absent
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Waiting List */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      En attente ({queueStatus?.waiting_count || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(queueStatus?.waiting_tickets || []).length === 0 ? (
                      <p className="text-center text-slate-500 py-4">Aucun patient en attente</p>
                    ) : (
                      <div className="space-y-2">
                        {queueStatus.waiting_tickets.map((ticket, index) => (
                          <div
                            key={ticket.id}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                                index === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'
                              }`}>
                                {ticket.ticket_number}
                              </div>
                              <div>
                                <p className="font-medium">{ticket.patient_name}</p>
                                {ticket.patient_phone && (
                                  <p className="text-xs text-slate-500">{ticket.patient_phone}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-500">
                                ~{ticket.estimated_wait_minutes || (index + 1) * selectedQueue.estimated_service_time} min
                              </span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleUpdateTicketStatus(ticket.id, 'cancelled')}
                              >
                                <XCircle className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Queue Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Créer une file d&apos;attente</DialogTitle>
            <DialogDescription>
              Configurez une nouvelle file avec QR code
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateQueue} className="space-y-4 mt-4">
            <div>
              <Label>Nom de la file *</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Ex: Consultation générale"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label>Type de file *</Label>
              <Select
                value={createForm.queue_type}
                onValueChange={(v) => setCreateForm({ ...createForm, queue_type: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="facility">Établissement (général)</SelectItem>
                  <SelectItem value="service">Service spécifique</SelectItem>
                  <SelectItem value="doctor">Médecin individuel</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {createForm.queue_type === 'service' && (
              <div>
                <Label>Nom du service *</Label>
                <Input
                  value={createForm.service_name}
                  onChange={(e) => setCreateForm({ ...createForm, service_name: e.target.value })}
                  placeholder="Ex: Urgences, Pharmacie, Imagerie"
                  className="mt-1"
                />
              </div>
            )}

            {createForm.queue_type === 'doctor' && (
              <div>
                <Label>Médecin *</Label>
                <Select
                  value={createForm.doctor_id}
                  onValueChange={(v) => {
                    const pro = professionals.find(p => p.id === v);
                    setCreateForm({
                      ...createForm,
                      doctor_id: v,
                      doctor_name: pro?.user?.full_name || ''
                    });
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Sélectionner un médecin" />
                  </SelectTrigger>
                  <SelectContent>
                    {professionals.map(pro => (
                      <SelectItem key={pro.id} value={pro.id}>
                        Dr. {pro.user?.full_name} - {pro.specialty}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Temps moyen (min)</Label>
                <Input
                  type="number"
                  value={createForm.estimated_service_time}
                  onChange={(e) => setCreateForm({ ...createForm, estimated_service_time: e.target.value })}
                  min="1"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Capacité max</Label>
                <Input
                  type="number"
                  value={createForm.max_capacity}
                  onChange={(e) => setCreateForm({ ...createForm, max_capacity: e.target.value })}
                  placeholder="Illimitée"
                  min="1"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label>File active</Label>
                <p className="text-xs text-slate-500">Accepte les nouveaux tickets</p>
              </div>
              <Switch
                checked={createForm.is_active}
                onCheckedChange={(checked) => setCreateForm({ ...createForm, is_active: checked })}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={creating}
                className="flex-1 bg-red-500 hover:bg-red-600"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>QR Code - {selectedQueue?.name}</DialogTitle>
            <DialogDescription>
              Les patients peuvent scanner ce code pour prendre un ticket
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center py-4">
            {selectedQueue?.qr_code ? (
              <>
                <div className="bg-white p-4 rounded-xl shadow-lg border-2 border-[#DC2626]">
                  <img
                    src={`data:image/png;base64,${selectedQueue.qr_code}`}
                    alt="QR Code"
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-4 text-center break-all">
                  {selectedQueue.qr_url}
                </p>
                <div className="flex gap-2 w-full mt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={downloadQRCode}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Image
                  </Button>
                  <Button
                    className="flex-1 bg-[#1A237E] hover:bg-[#0D1642]"
                    onClick={printQRCode}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Imprimer PDF
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-slate-500">QR Code non disponible</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats Modal */}
      <Dialog open={showStatsModal} onOpenChange={setShowStatsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Statistiques - {selectedQueue?.name}</DialogTitle>
            <DialogDescription>
              7 derniers jours
            </DialogDescription>
          </DialogHeader>

          {queueStats ? (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{queueStats.stats.total_tickets}</p>
                    <p className="text-xs text-slate-500">Total tickets</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{queueStats.stats.completion_rate}%</p>
                    <p className="text-xs text-slate-500">Taux complétion</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-amber-600">{queueStats.stats.avg_wait_time_minutes} min</p>
                    <p className="text-xs text-slate-500">Attente moyenne</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-red-600">{queueStats.stats.no_show_rate}%</p>
                    <p className="text-xs text-slate-500">Taux absence</p>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Volume quotidien</h4>
                <div className="space-y-1">
                  {Object.entries(queueStats.daily_breakdown || {}).slice(-7).map(([date, data]) => (
                    <div key={date} className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">{new Date(date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-slate-100 rounded-full h-2">
                          <div
                            className="bg-green-500 rounded-full h-2"
                            style={{ width: `${data.total > 0 ? (data.completed / data.total) * 100 : 0}%` }}
                          />
                        </div>
                        <span className="font-medium">{data.total}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilityQueue;
